/**
 * 환경설정 — 로그인 기관 사이드바 로고 등록 (라이트/다크 모드별 2종)
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { theme as staticTheme } from 'styles/theme';
import { ActionButton } from 'styles/common.styles';
import {
  loadTenantLogo,
  fetchTenantLogo,
  saveTenantLogo,
  removeTenantLogo,
  prepareTenantLogoFromFile,
  formatLogoBytes,
  TENANT_LOGO_INPUT_MAX_BYTES,
} from 'system/tenantLogoStore';
import { showAlert } from 'utils/toastBridge';

const LOGO_ACCEPT = 'image/png,image/jpeg,image/webp,image/svg+xml';

const Wrap = styled.section`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Hint = styled.p`
  margin: 0;
  font-size: ${staticTheme.typography.fontSize.xs};
  color: ${(p) => p.theme.colors.text.tertiary};
  line-height: 1.45;
`;

const TenantHint = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: ${staticTheme.borderRadius.sm};
  background: ${(p) => p.theme.colors.primaryLight};
  color: ${(p) => p.theme.colors.primary};
  font-size: ${staticTheme.typography.fontSize.sm};
  font-weight: 600;
  width: fit-content;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const Label = styled.label`
  min-width: 72px;
  font-size: ${staticTheme.typography.fontSize.sm};
  color: ${(p) => p.theme.colors.text.secondary};
  text-align: right;
`;

const SlotTitle = styled.div`
  min-width: 72px;
  font-size: ${staticTheme.typography.fontSize.sm};
  font-weight: 600;
  color: ${(p) => p.theme.colors.text.secondary};
  text-align: right;
`;

const TextInput = styled.input`
  min-width: 200px;
  height: 28px;
  padding: 0 8px;
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: ${staticTheme.borderRadius.sm};
  background: ${(p) => p.theme.colors.background.input};
  color: ${(p) => p.theme.colors.text.primary};
  font-size: ${staticTheme.typography.fontSize.sm};
`;

/* 라이트 슬롯은 밝은 배경, 다크 슬롯은 어두운 배경 고정 — 앱 테마와 무관하게 로고 대비를 확인한다 */
const PreviewBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 240px;
  height: 48px;
  padding: 6px 12px;
  border: 1px dashed ${(p) => p.theme.colors.border};
  border-radius: ${staticTheme.borderRadius.md};
  background: ${(p) => (p.$dark ? 'var(--erp-color-sidebar-dark, #1e293b)' : '#ffffff')};
`;

/* 사이드바 로고와 같은 규칙(높이 28px 채움, 가로 168px 이내)으로 실제 표시 모양을 보여준다 */
const PreviewImg = styled.img`
  height: 28px;
  max-width: 168px;
  width: auto;
  object-fit: contain;
  object-position: center;
`;

const SizeHint = styled.div`
  margin: 0;
  font-size: ${staticTheme.typography.fontSize.xs};
  color: ${(p) => p.theme.colors.text.secondary};
  line-height: 1.45;
`;

const EmptyPreview = styled.span`
  font-size: ${staticTheme.typography.fontSize.xs};
  color: ${(p) => p.theme.colors.text.tertiary};
`;

const HiddenFile = styled.input`
  display: none;
`;

const ToggleLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: ${staticTheme.typography.fontSize.sm};
  color: ${(p) => p.theme.colors.text.secondary};
  cursor: pointer;
  user-select: none;
`;

const WarnHint = styled(SizeHint)`
  color: ${(p) => p.theme.colors.text.tertiary};
`;

const renderInfo = (info) =>
  info ? (
    <div>
      <SizeHint>
        원본 {formatLogoBytes(info.originalBytes)}
        {info.width && info.height ? ` · 저장 ${info.width}×${info.height}px` : ''}
        {' → '}
        저장 {formatLogoBytes(info.storedBytes)}
        {info.format === 'svg' ? ' (SVG 원본 유지)' : info.resized ? ' (영역 맞춤)' : ''}
      </SizeHint>
      {info.trimNote ? <WarnHint>{info.trimNote}</WarnHint> : null}
      {info.lowRes ? (
        <WarnHint>해상도가 낮아 흐릿하게 보일 수 있습니다. 더 큰 원본을 권장합니다.</WarnHint>
      ) : null}
    </div>
  ) : null;

/**
 * @param {{ tenantCode?: string }} props
 */
const TenantLogoUploadPanel = ({ tenantCode = '' }) => {
  const lightFileRef = useRef(null);
  const darkFileRef = useRef(null);
  const code = String(tenantCode || '').trim().toUpperCase();
  const [logoAlt, setLogoAlt] = useState('');
  const [logoAreaDark, setLogoAreaDark] = useState(false);
  // 슬롯별 미리보기 = 저장 대기 dataURL
  const [lightUrl, setLightUrl] = useState('');
  const [darkUrl, setDarkUrl] = useState('');
  const [hasUpload, setHasUpload] = useState(false);
  const [picking, setPicking] = useState(false);
  const [lightInfo, setLightInfo] = useState(null);
  const [darkInfo, setDarkInfo] = useState(null);
  const [autoTrim, setAutoTrim] = useState(true);
  // 이번 화면에서 새로 고른 원본 File — 여백 제거 토글 시 재처리에 쓴다 (저장본에서 불러온 슬롯은 없음)
  const sourceFiles = useRef({ light: null, dark: null });
  // 슬롯별 요청 번호 — 늦게 끝난 이전 처리가 최신 선택을 덮어쓰지 않도록
  const reqSeq = useRef({ light: 0, dark: 0 });

  const refreshFromStore = useCallback((orgCode) => {
    const entry = loadTenantLogo(orgCode);
    setLightUrl(entry?.logoDataUrl || '');
    setDarkUrl(entry?.logoDarkDataUrl || '');
    setLogoAlt(entry?.logoDataUrl ? entry.logoAlt || orgCode : '');
    setLogoAreaDark(entry?.logoAreaDark === true);
    setHasUpload(!!entry?.logoDataUrl);
    setLightInfo(null);
    setDarkInfo(null);
    sourceFiles.current = { light: null, dark: null };
    reqSeq.current = { light: reqSeq.current.light + 1, dark: reqSeq.current.dark + 1 };
    setPicking(false);
  }, []);

  useEffect(() => {
    if (!code) return;
    refreshFromStore(code);
    fetchTenantLogo(code).then(() => refreshFromStore(code));
  }, [code, refreshFromStore]);

  /** 원본 File → 슬롯 미리보기 반영. 뒤늦게 끝난 이전 요청은 버린다. */
  const processFile = useCallback(async (slot, file, trim) => {
    const seq = reqSeq.current[slot] + 1;
    reqSeq.current[slot] = seq;
    setPicking(true);
    try {
      const prepared = await prepareTenantLogoFromFile(file, { autoTrim: trim });
      if (reqSeq.current[slot] !== seq) return;
      if (slot === 'dark') {
        setDarkUrl(prepared.dataUrl);
        setDarkInfo(prepared);
      } else {
        setLightUrl(prepared.dataUrl);
        setLightInfo(prepared);
      }
    } catch (err) {
      if (reqSeq.current[slot] === seq) showAlert(err?.message || '이미지를 읽지 못했습니다.');
    } finally {
      if (reqSeq.current[slot] === seq) setPicking(false);
    }
  }, []);

  /** @param {'light'|'dark'} slot */
  const handlePickFile = useCallback(
    (slot) => async (e) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file) return;
      sourceFiles.current[slot] = file;
      if (slot === 'light' && !logoAlt) setLogoAlt(code);
      await processFile(slot, file, autoTrim);
    },
    [logoAlt, code, autoTrim, processFile]
  );

  const handleToggleAutoTrim = useCallback(
    (e) => {
      const next = e.target.checked;
      setAutoTrim(next);
      ['light', 'dark'].forEach((slot) => {
        const file = sourceFiles.current[slot];
        if (file) processFile(slot, file, next);
      });
    },
    [processFile]
  );

  // 다크 슬롯만 비운다 — [로고 저장]을 눌러야 저장본에서 제거된다
  const handleClearDark = useCallback(() => {
    sourceFiles.current.dark = null;
    reqSeq.current.dark += 1;
    setPicking(false);
    setDarkUrl('');
    setDarkInfo(null);
  }, []);

  const handleSave = useCallback(() => {
    if (!code) {
      showAlert('기관코드가 없습니다. 다시 로그인해 주세요.');
      return;
    }
    if (!lightUrl) {
      showAlert('업로드할 이미지를 선택하세요. (라이트 로고는 필수입니다)');
      return;
    }
    const result = saveTenantLogo(code, {
      logoDataUrl: lightUrl,
      logoDarkDataUrl: darkUrl,
      logoAlt: logoAlt || code,
      logoAreaDark,
    });
    if (!result.ok) {
      showAlert(result.message || '저장에 실패했습니다.');
      return;
    }
    refreshFromStore(code);
    showAlert('로고를 저장했습니다. 사이드바에 바로 반영됩니다.');
  }, [code, lightUrl, darkUrl, logoAlt, logoAreaDark, refreshFromStore]);

  const handleRemove = useCallback(() => {
    if (!code) return;
    removeTenantLogo(code);
    refreshFromStore(code);
    showAlert('업로드 로고를 삭제했습니다. (정적 기본 로고가 있으면 그로 표시됩니다)');
  }, [code, refreshFromStore]);

  return (
    <Wrap>
      <Hint>
        로그인 기관의 사이드바 상단 CI만 등록합니다. PNG(투명 배경 권장) · JPG · WebP · SVG.
        원본은 {formatLogoBytes(TENANT_LOGO_INPUT_MAX_BYTES)}까지 올릴 수 있고, 저장 시 로고 영역에 맞춰
        크기·용량을 줄입니다. 업로드본이 정적 기본 로고보다 우선합니다.
        다크 모드용 로고는 선택이며, 없으면 라이트 로고를 그대로 사용합니다.
        이미지 주변의 투명·단색 여백은 자동으로 잘라내고 로고 영역 높이에 맞춰 표시합니다.
      </Hint>

      <TenantHint>기관 {code || '—'}</TenantHint>

      <Row>
        <Label htmlFor="tenant-logo-alt">표시명</Label>
        <TextInput
          id="tenant-logo-alt"
          value={logoAlt}
          onChange={(e) => setLogoAlt(e.target.value)}
          placeholder="로고 alt / 툴팁"
        />
        <ToggleLabel>
          <input
            type="checkbox"
            checked={logoAreaDark}
            onChange={(e) => setLogoAreaDark(e.target.checked)}
          />
          로고 행 다크 플레이트 (다크 로고가 없을 때 사용)
        </ToggleLabel>
        <ToggleLabel>
          <input type="checkbox" checked={autoTrim} onChange={handleToggleAutoTrim} />
          여백 자동 제거 (투명·단색 여백을 잘라 로고 영역에 꽉 채움)
        </ToggleLabel>
      </Row>

      <Row>
        <SlotTitle>라이트 모드</SlotTitle>
        <PreviewBox $dark={logoAreaDark}>
          {lightUrl ? (
            <PreviewImg src={lightUrl} alt={logoAlt || code || 'logo'} />
          ) : (
            <EmptyPreview>업로드된 로고 없음</EmptyPreview>
          )}
        </PreviewBox>
        {renderInfo(lightInfo)}
      </Row>
      <Row>
        <Label />
        <HiddenFile
          ref={lightFileRef}
          type="file"
          accept={LOGO_ACCEPT}
          onChange={handlePickFile('light')}
        />
        <ActionButton type="button" onClick={() => lightFileRef.current?.click()} disabled={!code || picking}>
          {picking ? '처리 중' : '라이트 이미지 선택'}
        </ActionButton>
      </Row>

      <Row>
        <SlotTitle>다크 모드</SlotTitle>
        <PreviewBox $dark>
          {darkUrl ? (
            <PreviewImg src={darkUrl} alt={`${logoAlt || code || 'logo'} (dark)`} />
          ) : (
            <EmptyPreview>없음 (라이트 로고 사용)</EmptyPreview>
          )}
        </PreviewBox>
        {renderInfo(darkInfo)}
      </Row>
      <Row>
        <Label />
        <HiddenFile
          ref={darkFileRef}
          type="file"
          accept={LOGO_ACCEPT}
          onChange={handlePickFile('dark')}
        />
        <ActionButton type="button" onClick={() => darkFileRef.current?.click()} disabled={!code || picking}>
          {picking ? '처리 중' : '다크 이미지 선택'}
        </ActionButton>
        <ActionButton type="button" onClick={handleClearDark} disabled={!code || !darkUrl}>
          다크 로고 삭제
        </ActionButton>
      </Row>

      <Row>
        <Label />
        <ActionButton type="button" $variant="primary" onClick={handleSave} disabled={!code || !lightUrl}>
          로고 저장
        </ActionButton>
        <ActionButton type="button" onClick={handleRemove} disabled={!code || !hasUpload}>
          업로드 삭제
        </ActionButton>
      </Row>
    </Wrap>
  );
};

export default TenantLogoUploadPanel;
